import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Play, ArrowRight, Heart, Gift, Sunrise, Flame, BookOpen,
  Sparkles, HandHeart, Church, Calendar, ChevronRight, Quote, Youtube,
  Compass, HeartHandshake, Megaphone,
} from 'lucide-react';
import { BRAND, HERO_IMAGES, PILLARS, DAILY_DOSAGES, TEACHINGS, TESTIMONIALS, FOUNDERS, EVENTS, VISION_MISSION, MANDATES } from '../mock';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SectionHeading from '../components/SectionHeading';
import VideoModal from '../components/VideoModal';
import VigilCountdown from '../components/VigilCountdown';

const ICONS = { Heart, Gift, Sunrise, Flame, Compass, HeartHandshake, Sparkles, Megaphone };

export default function Home() {
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  const openVideo = (v) => { setActiveVideo(v); setVideoOpen(true); };

  const featuredTeachings = TEACHINGS.slice(0, 4);
  const todaysDosage = DAILY_DOSAGES[0];

  return (
    <div className="overflow-hidden">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[100vh] flex items-center pt-24 pb-16">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 animate-kenburns">
            <img
              src={HERO_IMAGES.crossSunset}
              alt="Cross at sunset"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/90 via-[#0B1B27]/70 to-[#0B1B27]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B27]/70 via-transparent to-transparent" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
                <Sparkles className="h-3.5 w-3.5 text-gold-brand" />
                <span className="text-xs tracking-[0.28em] uppercase font-medium">Christ-Centred &middot; Interdenominational</span>
              </div>
            </div>

            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] animate-fade-in-up delay-100">
              Encounter Jesus.<br />
              Receive His <span className="italic text-gold-brand">grace</span>.<br />
              Walk in <span className="italic">hope</span>.
            </h1>

            <p className="mt-6 text-lg md:text-xl text-white/85 leading-relaxed max-w-2xl animate-fade-in-up delay-200">
              At <span className="font-semibold text-white">Dispensers of Grace Ministry</span>, we hold out the Word of God as living medicine for the soul &mdash; a daily prescription of love, hope, and holiness for a world in need of healing.
            </p>

            <div className="mt-6 animate-fade-in-up delay-200">
              <p className="font-script text-3xl md:text-4xl text-gold-brand">&ldquo;God prescribes His Word, we dispense it.&rdquo;</p>
            </div>

            <div className="mt-10 flex flex-wrap gap-3 animate-fade-in-up delay-300">
              <Link to="/about">
                <Button size="lg" className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold px-6 h-12">
                  Explore the Ministry <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button
                size="lg"
                onClick={() => openVideo(TEACHINGS[0])}
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-6 h-12 backdrop-blur-sm"
              >
                <Play className="mr-2 h-4 w-4 fill-white" /> Watch Teachings
              </Button>
              <Link to="/dosage">
                <Button size="lg" variant="outline" className="bg-transparent hover:bg-white/10 border-white/40 text-white font-semibold px-6 h-12">
                  <BookOpen className="mr-2 h-4 w-4" /> Read Today&rsquo;s Dosage
                </Button>
              </Link>
              <Link to="/prayer">
                <Button size="lg" variant="outline" className="bg-transparent hover:bg-white/10 border-white/40 text-white font-semibold px-6 h-12">
                  <HandHeart className="mr-2 h-4 w-4" /> Join Us in Prayer
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Video card */}
          <div className="lg:col-span-5 animate-fade-in-up delay-300">
            <div className="relative group cursor-pointer" onClick={() => openVideo(TEACHINGS[0])}>
              <div className="absolute -inset-1 bg-gradient-to-br from-gold-brand/40 to-teal-brand/40 rounded-2xl blur-xl opacity-60 group-hover:opacity-90 transition-opacity" />
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <video
                  src={BRAND.welcomeVideo}
                  className="w-full h-full object-cover"
                  autoPlay muted loop playsInline
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-20 w-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                    <Play className="h-8 w-8 text-teal-deep fill-teal-deep ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <p className="text-xs tracking-[0.28em] uppercase text-gold-brand mb-2">Welcome Message</p>
                  <p className="font-serif-display text-xl leading-tight">A word from Pastor Babalola</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/60">
          <span className="text-[10px] tracking-[0.35em] uppercase">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent" />
        </div>
      </section>

      {/* ============ WELCOME / MISSION STRIP ============ */}
      <section className="relative py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-xl">
              <img src={HERO_IMAGES.bibleSunrise} alt="Open Bible at sunrise" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -right-6 w-48 aspect-square rounded-2xl overflow-hidden shadow-2xl border-8 border-cream">
              <img src={HERO_IMAGES.crossSunset2} alt="cross" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -top-6 -left-6 bg-white rounded-xl p-4 shadow-xl max-w-[220px]">
              <Quote className="h-5 w-5 text-gold-brand mb-2" />
              <p className="font-serif-display italic text-sm text-ink/80 leading-snug">&ldquo;Freely you have received; freely give.&rdquo;</p>
              <p className="text-xs text-ink/50 mt-2">Matthew 10:8</p>
            </div>
          </div>

          <div>
            <SectionHeading
              eyebrow="Our Heart"
              title={<>Carrying the <span className="italic text-teal-deep">Transforming Power</span> of Grace</>}
              align="left"
            />
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-ink/75">
              <p>{VISION_MISSION.intro}</p>
              <div className="pl-5 border-l-2 border-gold-brand space-y-3">
                <div>
                  <p className="text-xs tracking-[0.28em] uppercase text-gold-deep font-medium mb-1">Vision</p>
                  <p className="font-serif-display italic text-xl text-teal-deep">{VISION_MISSION.vision}</p>
                </div>
                <div>
                  <p className="text-xs tracking-[0.28em] uppercase text-gold-deep font-medium mb-1">Mission</p>
                  <p className="text-ink/80">{VISION_MISSION.mission}</p>
                </div>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about">
                <Button className="bg-teal-brand hover:bg-teal-deep text-white font-semibold h-11 px-5">
                  Learn More About Us <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Button variant="outline" onClick={() => openVideo(TEACHINGS[1])} className="h-11 px-5 border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white">
                <Play className="mr-2 h-4 w-4" /> Ministry Promo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOUR PILLARS ============ */}
      <section className="relative py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="What We Stand For"
            title={<>Four Pillars of the <span className="italic text-teal-deep">Christ-Life</span></>}
            subtitle="Every teaching, every prayer, every service flows from these Scriptural convictions."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p, i) => {
              const Icon = ICONS[p.icon] || Heart;
              return (
                <Card key={p.title} className="hover-lift group p-8 bg-white border border-ink/5 rounded-2xl shadow-[0_10px_30px_-20px_rgba(15,34,49,0.15)]">
                  <div className="h-14 w-14 rounded-xl bg-cream flex items-center justify-center mb-6 group-hover:bg-gold-brand transition-colors">
                    <Icon className="h-6 w-6 text-teal-deep group-hover:text-ink transition-colors" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif-display text-2xl text-ink mb-2">{p.title}</h3>
                  <p className="text-xs tracking-[0.25em] uppercase text-gold-deep font-medium mb-3">{p.verse}</p>
                  <p className="text-sm text-ink/70 leading-relaxed">{p.text}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ OUR MANDATES ============ */}
      <section className="relative py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="Our Mandates"
            title={<>Five Focus Areas of <span className="italic text-teal-deep">Grace</span></>}
            subtitle="The specific assignments the Great Physician has entrusted to Dispensers of Grace Ministry."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {MANDATES.map((m) => {
              const Icon = ICONS[m.icon] || Heart;
              return (
                <div key={m.title} className="hover-lift group p-6 bg-white border border-ink/5 rounded-2xl text-center">
                  <div className="h-14 w-14 mx-auto rounded-xl bg-cream flex items-center justify-center mb-4 group-hover:bg-gold-brand transition-colors">
                    <Icon className="h-6 w-6 text-teal-deep group-hover:text-ink transition-colors" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif-display text-xl text-ink mb-2">{m.title}</h3>
                  <p className="text-sm text-ink/65 leading-relaxed">{m.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ TODAY'S DOSAGE ============ */}
      <section className="relative py-20 md:py-28 bg-[#0F2231] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={HERO_IMAGES.bibleStudy} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2231] via-[#0F2231]/85 to-[#0F2231]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading
              eyebrow="Today's Dosage"
              title={<>A Daily <span className="italic text-gold-brand">Prescription</span> from the Word</>}
              subtitle="A short, Scripture-rooted devotional to steady your soul and set your day in Christ. Take it daily."
              align="left"
              light
            />
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3 text-sm text-white/70">
                <Badge className="bg-gold-brand text-ink font-semibold">{todaysDosage.date}</Badge>
                <span>&middot;</span>
                <span>{todaysDosage.minutes} min read</span>
                <span>&middot;</span>
                <span className="text-gold-brand">{todaysDosage.verseRef}</span>
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">{todaysDosage.title}</h3>
              <blockquote className="font-serif-display italic text-xl text-white/85 border-l-2 border-gold-brand pl-5">
                &ldquo;{todaysDosage.verse}&rdquo;
              </blockquote>
              <p className="text-white/75 leading-relaxed">{todaysDosage.body}</p>
            </div>
            <div className="mt-8 flex gap-3">
              <Link to="/dosage">
                <Button className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11 px-5">
                  Read Full Dosage <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/dosage">
                <Button variant="outline" className="h-11 px-5 bg-transparent border-white/30 text-white hover:bg-white/10">
                  Browse Archive
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid gap-4">
            {DAILY_DOSAGES.slice(1, 4).map((d) => (
              <Link to="/dosage" key={d.id} className="group flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold-brand/40 transition-all">
                <img src={d.image} alt={d.title} className="h-20 w-20 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs tracking-[0.2em] uppercase text-gold-brand mb-1">{d.date} &middot; {d.verseRef}</p>
                  <p className="font-serif-display text-lg leading-snug group-hover:text-gold-brand transition-colors">{d.title}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-white/40 group-hover:text-gold-brand group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TEACHINGS ============ */}
      <section className="relative py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <SectionHeading
                eyebrow="Watch & Grow"
                title={<>Teachings from the <span className="italic text-teal-deep">Heart of Scripture</span></>}
                subtitle="Messages that lift Jesus high, unpack the Word plainly, and call every heart into deeper communion."
                align="left"
              />
            </div>
            <Link to="/teachings">
              <Button variant="outline" className="border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white h-11">
                View All Teachings <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredTeachings.map((t) => (
              <Card key={t.id} className="hover-lift group overflow-hidden bg-white rounded-2xl border border-ink/5 cursor-pointer" onClick={() => t.isLocal ? openVideo(t) : null}>
                <div className="relative aspect-video overflow-hidden bg-black">
                  <img src={t.thumbnail} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-14 w-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="h-6 w-6 text-teal-deep fill-teal-deep ml-0.5" />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-gold-brand text-ink font-medium">{t.series}</Badge>
                  </div>
                  <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/60 text-white text-xs font-medium">{t.duration}</div>
                </div>
                <div className="p-5">
                  <h3 className="font-serif-display text-xl leading-snug text-ink group-hover:text-teal-deep transition-colors">{t.title}</h3>
                  <p className="text-xs tracking-[0.2em] uppercase text-ink/50 mt-2">{t.speaker}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ LIVE ON YOUTUBE ============ */}
      <section className="relative py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-100 mb-5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF0000] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF0000]" />
                </span>
                <span className="text-xs tracking-[0.28em] uppercase font-semibold text-[#B10000]">Live on YouTube</span>
              </div>
              <SectionHeading
                eyebrow="Watch Now"
                title={<>Join our <span className="italic text-teal-deep">Live Broadcast</span></>}
                subtitle="Experience the Word in real time — worship, teaching, and prayer, streamed straight to you."
                align="left"
              />
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={BRAND.youtube} target="_blank" rel="noreferrer">
                  <Button className="bg-[#FF0000] hover:bg-[#CC0000] text-white font-semibold h-11 px-5">
                    <Youtube className="mr-2 h-4 w-4" /> Watch on YouTube
                  </Button>
                </a>
                <a href={BRAND.youtube} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="h-11 px-5 border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white">
                    Subscribe to the Channel
                  </Button>
                </a>
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video bg-black">
                <iframe
                  src={BRAND.youtubeEmbed}
                  title="Dispensers of Grace Live"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOUNDERS ============ */}
      <section className="relative py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="Meet the Shepherds"
            title={<>Our <span className="italic text-teal-deep">Founders</span></>}
            subtitle="A husband and wife given together to the ministry of the Word, prayer, and the shepherding of souls into the fullness of Christ."
          />
          <div className="mt-14 grid md:grid-cols-2 gap-8 lg:gap-12">
            {FOUNDERS.map((f, i) => (
              <Card key={f.name} className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5 shadow-[0_10px_40px_-24px_rgba(15,34,49,0.25)] hover-lift">
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="relative w-32 h-40 sm:w-36 sm:h-44 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-teal-brand/20 to-gold-brand/20">
                    {f.photo ? (
                      <img src={f.photo} alt={f.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-teal-deep">
                        <span className="font-serif-display text-4xl">{f.name.split(' ').filter(w => w.length > 3).slice(-2).map(w => w[0]).join('')}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs tracking-[0.25em] uppercase text-gold-deep mb-2">{f.role}</p>
                    <h3 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">{f.name}</h3>
                    <p className="mt-4 text-ink/70 leading-relaxed">{f.bio}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/about">
              <Button className="bg-teal-brand hover:bg-teal-deep text-white font-semibold h-11 px-6">
                Read More About the Ministry <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIES ============ */}
      <section className="relative py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="Stories of Grace"
            title={<>Lives Being <span className="italic text-teal-deep">Rewritten</span> by Jesus</>}
            subtitle="A few voices from our global family — testimonies of the God who still heals, still saves, still speaks."
          />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <Card key={t.id} className="p-8 bg-white rounded-2xl border border-ink/5 hover-lift">
                <Quote className="h-8 w-8 text-gold-brand mb-4" />
                <p className="font-serif-display italic text-xl leading-relaxed text-ink/85">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-6 pt-6 border-t border-ink/10">
                  <p className="font-semibold text-ink">{t.name}</p>
                  <p className="text-sm text-ink/50">{t.role}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ UPCOMING EVENTS ============ */}
      <VigilCountdown />
      <section className="relative py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <SectionHeading
              eyebrow="Come, Gather"
              title={<>Join Us <span className="italic text-teal-deep">In Worship</span></>}
              subtitle="Weekly services and special gatherings — online and in-person, all in Jesus' name."
              align="left"
            />
            <Link to="/events">
              <Button variant="outline" className="border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white h-11">
                All Events <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {EVENTS.slice(0, 4).map((e) => (
              <Card key={e.id} className="p-6 bg-white rounded-2xl border border-ink/5 hover-lift flex gap-5">
                <div className="flex flex-col items-center justify-center h-16 w-16 rounded-xl bg-teal-deep text-white flex-shrink-0">
                  <Calendar className="h-6 w-6 text-gold-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="bg-cream text-teal-deep font-medium">{e.tag}</Badge>
                  </div>
                  <h3 className="font-serif-display text-xl text-ink leading-snug">{e.title}</h3>
                  <p className="text-sm text-ink/60 mt-1">{e.date} &middot; {e.time}</p>
                  <p className="text-sm text-ink/60">{e.location}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.worshipHands} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-deep/95 via-teal-deep/85 to-teal-deep/60" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-6 text-center text-white">
          <Church className="h-10 w-10 text-gold-brand mx-auto mb-6" strokeWidth={1.4} />
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight">
            Come and see what Jesus is doing.
          </h2>
          <p className="mt-6 text-lg text-white/85 max-w-2xl mx-auto">
            Whether you are seeking, hurting, or hungry for more of God &mdash; there is a place for you here. Reach out. We&rsquo;d love to pray with you.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/prayer"><Button size="lg" className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-12 px-6">Submit a Prayer Request</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="bg-transparent hover:bg-white/10 border-white/40 text-white h-12 px-6">Get in Touch</Button></Link>
          </div>
        </div>
      </section>

      <VideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoUrl={activeVideo?.videoUrl}
        title={activeVideo?.title}
        isLocal={activeVideo?.isLocal}
      />
    </div>
  );
}
